import {
  CustomWallet,
  Exchange,
  UserFreezingStatus,
  Wallet,
} from '@cointracker/graphql-types';
import { Dayjs } from 'dayjs';
import englishLocale from 'dayjs/locale/en';
import dayjs from 'src/app/utils/dayjsConfig';
import firstWalletIllustrationUrl from 'src/assets/first-wallet-illustration.webp?url';
import somethingWentWrongIllustration from 'src/assets/something-went-wrong-illustration.webp?url';
import upToDateIllustrationUrl from 'src/assets/up-to-date-illustration.webp?url';
import freezingIllustration from '../../../../pages/Rebrand/Tax/FreezingBanner/images/image-freezing.webp?url';

import { t } from 'i18next';
import { StatusReadyState } from './hooks/useGetStatus';

export const getStatusText = (
  status: StatusReadyState,
  freezingStatus: UserFreezingStatus,
) => {
  switch (status) {
    case StatusReadyState.Syncing:
      return t('navigation.status_section.syncing.title');
    case StatusReadyState.Calculating:
      return t('navigation.status_section.calculating.title');
    case StatusReadyState.Failed:
      return t('navigation.status_section.failed.title');
    case StatusReadyState.Loading:
      return t('navigation.status_section.loading');
    case StatusReadyState.Paused:
      return t('navigation.status_section.paused.title');
    case StatusReadyState.EmptyData:
      return t('navigation.status_section.empty_data.title');
    case StatusReadyState.Ready:
    default: {
      if (freezingStatus === UserFreezingStatus.FreezingInProgress) {
        return t('navigation.status_section.freezing_in_progress.title');
      }
      if (freezingStatus === UserFreezingStatus.UnfreezingInProgress) {
        return t('navigation.status_section.unfreezing_in_progress.title');
      }
      if (
        freezingStatus === UserFreezingStatus.ErroredWithFrozenPeriods ||
        freezingStatus === UserFreezingStatus.ErroredWithUnfrozenPeriods
      ) {
        return t('navigation.status_section.freezing_error.title');
      }
      return t('navigation.status_section.up_to_date.title');
    }
  }
};

export const getExpandedStatusText = (
  status: StatusReadyState,
  freezingStatus: UserFreezingStatus,
) => {
  switch (status) {
    case StatusReadyState.Syncing:
      return t('navigation.status_section.syncing.title');
    case StatusReadyState.Calculating:
      return t('navigation.status_section.calculating.title');
    case StatusReadyState.Failed:
      return t('errors.something_went_wrong');
    case StatusReadyState.Loading:
      return t('navigation.status_section.loading');
    case StatusReadyState.Paused:
      return t('navigation.status_section.paused.expanded_title');
    case StatusReadyState.EmptyData:
      return t('navigation.status_section.empty_data.expanded_title');
    case StatusReadyState.Ready:
    default: {
      if (freezingStatus === UserFreezingStatus.FreezingInProgress) {
        return t(
          'navigation.status_section.freezing_in_progress.expanded_title',
        );
      }
      if (freezingStatus === UserFreezingStatus.UnfreezingInProgress) {
        return t(
          'navigation.status_section.unfreezing_in_progress.expanded_title',
        );
      }
      if (
        freezingStatus === UserFreezingStatus.ErroredWithFrozenPeriods ||
        freezingStatus === UserFreezingStatus.ErroredWithUnfrozenPeriods
      ) {
        return t('errors.something_went_wrong');
      }
      return t('navigation.status_section.up_to_date.title');
    }
  }
};

export const getExpandedStatusImage = (
  status: StatusReadyState,
  freezingStatus: UserFreezingStatus,
) => {
  switch (status) {
    case StatusReadyState.Syncing:
      return null;
    case StatusReadyState.Calculating:
      return null;
    case StatusReadyState.Failed:
      return {
        src: somethingWentWrongIllustration,
        alt: 'Image depicting something went wrong',
      };
    case StatusReadyState.Loading:
      return null;
    case StatusReadyState.Paused:
      return null;
    case StatusReadyState.EmptyData:
      return {
        src: firstWalletIllustrationUrl,
        alt: 'Image depicting empty wallet data',
      };
    case StatusReadyState.Ready:
    default: {
      if (
        freezingStatus === UserFreezingStatus.FreezingInProgress ||
        freezingStatus === UserFreezingStatus.UnfreezingInProgress
      ) {
        return {
          src: freezingIllustration,
          alt: 'Image depicting freezing transactions',
        };
      }

      if (
        freezingStatus === UserFreezingStatus.ErroredWithFrozenPeriods ||
        freezingStatus === UserFreezingStatus.ErroredWithUnfrozenPeriods
      ) {
        return {
          src: somethingWentWrongIllustration,
          alt: 'Image depicting something went wrong',
        };
      }
      return {
        src: upToDateIllustrationUrl,
        alt: 'Image depicting up to date wallet data',
      };
    }
  }
};

export const getMostRecentlySyncedDate = (
  walletsOrAccounts: (Exchange | Wallet | CustomWallet)[],
) => {
  const mostRecentlySyncedDate = walletsOrAccounts.reduce(
    (acc: Dayjs, walletOrAccount) => {
      if (!walletOrAccount.syncInfo.lastSynced) return acc;
      const currentSyncDayJs = dayjs(walletOrAccount.syncInfo.lastSynced);
      if (!acc) return currentSyncDayJs;
      return currentSyncDayJs.isAfter(acc) ? currentSyncDayJs : acc;
    },
    null,
  );
  return dayjs(mostRecentlySyncedDate).locale(englishLocale).fromNow();
};
